import {
  selectDeviceTypeMap,
  selectIntegrationFetching,
} from 'features/integrations/state/selectors/integrationSelectors';
import {
  defineGroupType,
  deviceListforGroup,
  isDeviceTypeAllowed,
} from 'features/integrations/utils/deviceGroupRulesUtils';
import { Accordion } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';

/**
 *
 * @param {{ value: number[], onChange: (value: number[], deviceType: string) }} param0
 * @returns
 */
export default function DeviceMultiSelector({ value, deviceType, onChange }) {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();

  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const integrationFetching = useSelector(selectIntegrationFetching);

  const formatDeviceTypeLabel = (label) => label?.replace(/_/g, ' ') || label;
  const safeDeviceTypes = deviceTypes && typeof deviceTypes === 'object' ? deviceTypes : {};
  const systemTypeKeys = Object.keys(safeDeviceTypes);
  const deviceTypesReady = systemTypeKeys.length > 0;

  // Toggle device selection while enforcing mutually exclusive type rules
  const selectDevice = (deviceId) => {
    //device list
    let newArr = [];

    if (!value.includes(deviceId)) newArr = [...value, deviceId];
    else newArr = value.filter((_deviceId) => _deviceId !== deviceId);

    //define/check group type. Fetch allowed devices based on type
    const typeStr = defineGroupType(newArr, connectedDevices || [], deviceType);
    newArr = deviceListforGroup(newArr, typeStr, connectedDevices || []);

    onChange(newArr, typeStr);
  };

  const checkType = (type, checkType = deviceType) => {
    return isDeviceTypeAllowed(type, checkType);
  };

  const filterDevices = (type) => {
    return (connectedDevices ?? []).filter((device) => device.device_type.includes(type));
  };

  if (connectedDevicesLoading || (!deviceTypesReady && integrationFetching)) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 text-secondary">
        <div className="spinner-border spinner-border-sm me-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        Loading devices...
      </div>
    );
  }

  if (connectedDevicesError) {
    return <div className="text-danger">Unable to load devices.</div>;
  }

  if (!deviceTypesReady) {
    return <div className="text-secondary">No devices available.</div>;
  }

  const systemSections = systemTypeKeys
    .map((systemType) => {
      const extractedDeviceTypes = Object.keys(safeDeviceTypes[systemType] || {});

      const deviceTypeSections = extractedDeviceTypes
        .map((rawDeviceType) => {
          const devicesForType = filterDevices(rawDeviceType);
          if (!devicesForType.length) return null;

          const selectedCount = devicesForType.filter((device) => value.includes(device.id)).length;

          return {
            id: `${systemType}::${rawDeviceType}`,
            label: formatDeviceTypeLabel(rawDeviceType),
            devices: devicesForType,
            selectedCount,
          };
        })
        .filter(Boolean);

      if (!deviceTypeSections.length) return null;

      return {
        systemType,
        deviceTypeSections,
      };
    })
    .filter(Boolean);

  if (!systemSections.length) {
    return <div className="text-secondary">No devices available.</div>;
  }

  return (
    <div className="d-flex flex-column gap-2 bg-primary rounded-3 border border-light border-opacity-25 p-2 h-100 overflow-auto">
      {systemSections.map((systemSection) => {
        const defaultOpenSections = systemSection.deviceTypeSections
          .filter((section) => section.selectedCount > 0)
          .map((section) => section.id);

        return (
          <div key={systemSection.systemType} className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center px-1">
              <div className="fw-semibold lh-sm">{systemSection.systemType}</div>
            </div>

            <Accordion
              alwaysOpen
              defaultActiveKey={defaultOpenSections}
              className="w-100 acc-theme-primary"
            >
              {systemSection.deviceTypeSections.map((section, sectionIdx) => (
                <Accordion.Item
                  eventKey={section.id}
                  key={section.id}
                  className={`border border-light border-opacity-25 rounded-2 overflow-hidden ${
                    sectionIdx < systemSection.deviceTypeSections.length - 1 ? 'mb-2' : ''
                  }`}
                >
                  <Accordion.Header>
                    <div className="d-flex w-100 align-items-center justify-content-between gap-2 pe-2">
                      <span className="text-break">{section.label}</span>
                      <span className="d-inline-flex align-items-center justify-content-center px-2 py-0 rounded-pill border border-light border-opacity-25 bg-dark bg-opacity-25 small text-nowrap">
                        {section.selectedCount} selected
                      </span>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body className="bg-transparent py-1 px-2">
                    {section.devices.map((device) => {
                      const canSelect = checkType(device.device_type);
                      const isSelected = value.includes(device.id);

                      return (
                        <div
                          key={`device-${device.id}`}
                          className={`d-flex align-items-center gap-2 px-2 py-1 rounded ${
                            canSelect ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'
                          }`}
                          onClick={() => {
                            if (canSelect) selectDevice(device.id);
                          }}
                        >
                          <input
                            type="checkbox"
                            readOnly
                            checked={isSelected}
                            disabled={!canSelect}
                          />
                          <div
                            className={`text-truncate flex-grow-1 ${!canSelect ? 'text-white-50' : ''}`}
                            style={{ minWidth: 0 }}
                          >
                            {device.alias || device.name || 'Unnamed Device'}
                          </div>
                        </div>
                      );
                    })}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </div>
        );
      })}
    </div>
  );
}
